import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

export interface ButtonDropdownOption {
  value: string | number;
  label: string;
}

@Component({
  selector: "app-button-dropdown",
  templateUrl: "./button-dropdown.component.html",
  styleUrls: ["./button-dropdown.component.scss"],
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    "[class.ln-dd-host-block]": "fullWidth",
  },
})
export class ButtonDropdownComponent {
  /** Leading font-awesome icon classes, e.g. "fa fa-building-o". Optional. */
  @Input() leadIcon?: string;
  /** Tints the leading icon with the accent (green) color. */
  @Input() leadIconAccent = false;
  /** Options as plain strings or `{ value, label }` objects. */
  @Input() options: readonly (string | ButtonDropdownOption)[] = [];
  /** Currently selected value (matched against option values). Single-select. */
  @Input() selectedValue: string | number | null = null;
  /** Currently selected values when `multiple` is true. */
  @Input() selectedValues: (string | number)[] = [];
  /** Allow toggling several options; menu stays open until closed elsewhere. */
  @Input() multiple = false;
  /**
   * When multiple values are selected, show this label on the trigger
   * instead of a comma-joined list of option labels.
   */
  @Input() multipleDisplayLabel?: string;
  /** Static text rendered before the selected label, e.g. "Auto Refresh: ". */
  @Input() labelPrefix = "";
  /** Overrides the computed label entirely when provided. */
  @Input() displayLabel?: string;
  /** Fallback text shown when nothing is selected. */
  @Input() placeholder = "Select";
  /** Menu horizontal alignment relative to the button. */
  @Input() menuAlign: "left" | "right" = "left";
  /** Minimum button width (any CSS length). Defaults to the live page width. */
  @Input() minWidth = "220px";
  @Input() minHeight = "36px";
  @Input() disabled = false;
  /** Shows a search box at the top of the menu that filters options by label. */
  @Input() searchable = false;
  /** Placeholder for the search box (only used when `searchable`). */
  @Input() searchPlaceholder = "Search...";
  /** Max height of the (scrollable) options list when `searchable`. */
  @Input() menuMaxHeight = "260px";
  /** Stretch the trigger to the width of its container. */
  @Input() fullWidth = false;
  /** Optional heading shown at the top of the menu. */
  @Input() menuHeading = "";
  /** Show a checkmark beside the selected option. */
  @Input() showCheck = false;

  /** Single-select: emits the chosen value. Multi-select: emits the full selection. */
  @Output() selectionChange = new EventEmitter<
    string | number | Array<string | number>
  >();
  /** Emitted when the menu is opened (not when closed). */
  @Output() menuOpen = new EventEmitter<void>();

  open = false;
  searchTerm = "";

  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  get normalizedOptions(): ButtonDropdownOption[] {
    return (this.options ?? []).map((option) =>
      typeof option === "string" ? { value: option, label: option } : option
    );
  }

  get filteredOptions(): ButtonDropdownOption[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.normalizedOptions;
    }
    return this.normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  }

  get buttonLabel(): string {
    if (this.displayLabel != null && this.displayLabel !== "") {
      return this.displayLabel;
    }
    if (this.multiple) {
      return `${this.labelPrefix}${this.multiButtonBaseLabel()}`;
    }
    const selected = this.normalizedOptions.find((option) =>
      this.isSameValue(option.value, this.selectedValue)
    );
    const base = selected ? selected.label : this.placeholder;
    return `${this.labelPrefix}${base}`;
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
    if (this.open) {
      this.searchTerm = "";
      this.menuOpen.emit();
      // Wait until Angular paints the menu, then scroll the panel if needed.
      setTimeout(() => this.ensureMenuFullyVisible(), 0);
    }
  }

  close(): void {
    this.open = false;
    this.searchTerm = "";
  }

  select(option: ButtonDropdownOption, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.multiple) {
      const current = [...(this.selectedValues ?? [])];
      const index = current.findIndex((value) =>
        this.isSameValue(option.value, value)
      );
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(option.value);
      }
      this.selectionChange.emit(current);
      return;
    }
    this.open = false;
    this.searchTerm = "";
    this.selectionChange.emit(option.value);
  }

  isActive(option: ButtonDropdownOption): boolean {
    if (this.multiple) {
      return (this.selectedValues ?? []).some((value) =>
        this.isSameValue(option.value, value)
      );
    }
    return this.isSameValue(option.value, this.selectedValue);
  }

  private isSameValue(
    optionValue: string | number,
    selectedValue: string | number | null
  ): boolean {
    return (
      selectedValue != null &&
      selectedValue !== "" &&
      String(optionValue) === String(selectedValue)
    );
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.hostRef.nativeElement.contains(target)) {
      this.open = false;
      this.searchTerm = "";
    }
  }

  private multiButtonBaseLabel(): string {
    const selected = this.normalizedOptions.filter((option) =>
      (this.selectedValues ?? []).some((value) =>
        this.isSameValue(option.value, value)
      )
    );
    if (selected.length === 0) {
      return this.placeholder;
    }
    if (selected.length === 1) {
      return selected[0].label;
    }
    if (this.multipleDisplayLabel != null) {
      return this.multipleDisplayLabel;
    }
    return selected.map((option) => option.label).join(", ");
  }

  /**
   * Keeps the menu opening below the trigger. If the panel would clip options,
   * auto-scrolls the nearest scroll container so the full list is visible —
   * users should not need to notice or use a scrollbar to discover options.
   */
  private ensureMenuFullyVisible(): void {
    if (!this.open) {
      return;
    }

    const host = this.hostRef.nativeElement;
    const menu = host.querySelector(".ln-dropdown__menu") as HTMLElement | null;
    if (!menu) {
      return;
    }

    const scrollParent = this.getScrollParent(host);
    const padding = 10;
    const menuRect = menu.getBoundingClientRect();

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const overflowBottom = menuRect.bottom - (parentRect.bottom - padding);
      if (overflowBottom > 0) {
        scrollParent.scrollBy({ top: overflowBottom, behavior: "smooth" });
      }
      return;
    }

    const overflowBottom = menuRect.bottom - (window.innerHeight - padding);
    if (overflowBottom > 0) {
      window.scrollBy({ top: overflowBottom, behavior: "smooth" });
    }
  }

  private getScrollParent(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }
}
